/** 工作区路径：斜杠统一、抽出 user_* 相对路径、定位到树节点。 */

const WORKSPACE_ROOT_NAMES = ["ybg-workspace"];
export const HIDDEN_TREE_NAMES = new Set([".claude", ".youlaber", ".cc-home", ".git"]);

export function slashPath(path) {
  return String(path || "").replace(/\\/g, "/").trim();
}

export function extractUserRelPath(path) {
  const slashed = slashPath(path);
  const match = slashed.match(/(?:^|\/)(user_[^/]+\/.+)$/);
  return match ? match[1] : "";
}

export function stripWorkspaceRootPrefix(path) {
  let next = slashPath(path).replace(/^[A-Za-z]:/, "").replace(/^\/+/, "");
  for (const root of WORKSPACE_ROOT_NAMES) {
    const prefix = `${root.toLowerCase()}/`;
    if (next.toLowerCase().startsWith(prefix)) {
      next = next.slice(root.length + 1);
    }
  }
  return next;
}

export function normalizeWorkspacePath(path) {
  if (!path) return "";
  const userRel = extractUserRelPath(path);
  if (userRel) return userRel;
  return stripWorkspaceRootPrefix(path);
}

export function sameWorkspacePath(a, b) {
  return normalizeWorkspacePath(a) === normalizeWorkspacePath(b);
}

export function ancestorDirPaths(filePath) {
  const parts = normalizeWorkspacePath(filePath).split("/").filter(Boolean);
  const dirs = [];
  let acc = "";
  for (let i = 0; i < parts.length - 1; i += 1) {
    acc = acc ? `${acc}/${parts[i]}` : parts[i];
    dirs.push(acc);
  }
  return dirs;
}

const pathKey = (path) => slashPath(path).replace(/^[A-Za-z]:/, "").replace(/^\/+/, "").toLowerCase();

export function findTreeNodeByPath(nodes, path) {
  const target = pathKey(path);
  if (!target) return null;
  const walk = (list) => {
    for (const node of list || []) {
      if (pathKey(node.path) === target) return node;
      const hit = walk(node.children);
      if (hit) return hit;
    }
    return null;
  };
  return walk(nodes);
}

function collectFileNodes(nodes, acc = []) {
  for (const node of nodes || []) {
    if (!node.is_dir) acc.push(node);
    collectFileNodes(node.children, acc);
  }
  return acc;
}

/**
 * 把卡片/工具里的路径解析成工作区相对路径。
 * 优先抽出 user_*，再剥工作区根名，再匹配目录树；树未刷新时也可按相对路径尝试打开。
 */
export function resolveWorkspaceRelPath(raw, treeNodes = []) {
  const slashed = slashPath(raw);
  if (!slashed) return { error: "路径为空" };

  const userRel = extractUserRelPath(slashed);
  const stripped = stripWorkspaceRootPrefix(slashed);
  const candidates = [...new Set(
    [userRel, stripped, normalizeWorkspacePath(slashed), slashed].filter(Boolean),
  )];

  for (const candidate of candidates) {
    const exact = findTreeNodeByPath(treeNodes, candidate);
    if (exact) {
      if (exact.is_dir) return { error: "这是目录，不能在编辑器中打开" };
      return { path: exact.path, name: exact.name };
    }
  }

  const targetKeys = [...new Set(candidates.map(pathKey).filter(Boolean))];
  const files = collectFileNodes(treeNodes);
  const hits = files.filter((node) => {
    const np = pathKey(node.path);
    return targetKeys.some((target) => (
      target === np || target.endsWith(`/${np}`) || np.endsWith(`/${target}`)
    ));
  });

  if (hits.length === 1) {
    return { path: hits[0].path, name: hits[0].name };
  }
  if (hits.length > 1) {
    hits.sort((a, b) => pathKey(b.path).length - pathKey(a.path).length);
    const bestLen = pathKey(hits[0].path).length;
    const longest = hits.filter((h) => pathKey(h.path).length === bestLen);
    if (longest.length === 1) return { path: longest[0].path, name: longest[0].name };
    return { error: "工作区中有多个同名路径，无法确定要打开的文件" };
  }

  const fallback = userRel || stripped;
  if (fallback && !fallback.includes("..") && !/^[A-Za-z]:/.test(fallback)) {
    return {
      path: fallback,
      name: fallback.split("/").pop() || fallback,
      unverified: true,
    };
  }

  return { error: "路径不在当前工作区" };
}

/** 树里对学生隐藏的内部目录（任意层级按目录名过滤）。 */
export function filterHiddenTreeNodes(nodes) {
  return (nodes || [])
    .filter((node) => !HIDDEN_TREE_NAMES.has(String(node?.name || "").toLowerCase()))
    .map((node) => (
      node.children
        ? { ...node, children: filterHiddenTreeNodes(node.children) }
        : node
    ));
}

/** skill 包：.claude/skills/** 及其工作区别名 skills/**，不对学生开放点击。 */
export function isSkillWorkspacePath(path) {
  const n = stripWorkspaceRootPrefix(path).toLowerCase();
  if (!n) return false;
  return n.startsWith(".claude/skills/") || n.includes("/.claude/skills/") || n.startsWith("skills/");
}

function trimPathToken(raw) {
  return String(raw || "").replace(/[.,;:，。；、]+$/g, "");
}

/**
 * 从助手正文抽出可点的学生作业文件路径（须带扩展名，且落在 user_* 下）。
 * skill 路径会被丢掉。
 */
export function extractClickableWorkspacePaths(text) {
  const src = String(text || "");
  if (!src) return [];

  const fileExt = "\\.[A-Za-z0-9]{1,12}";
  const stop = "[\\s`\"'<>|）)\\]】,，。；;!?？]";
  const patterns = [
    new RegExp(`[A-Za-z]:[\\\\/][^\\s\`"'<>|*?]+?${fileExt}(?=${stop}|$)`, "g"),
    new RegExp(`ybg-workspace[\\\\/][^\\s\`"'<>|*?]+?${fileExt}(?=${stop}|$)`, "gi"),
    new RegExp(`user_[^\\\\/\\s]+[\\\\/][^\\s\`"'<>|*?]+?${fileExt}(?=${stop}|$)`, "g"),
  ];

  const found = [];
  const seen = new Set();
  for (const re of patterns) {
    re.lastIndex = 0;
    let m = re.exec(src);
    while (m) {
      const raw = trimPathToken(m[0]);
      const userRel = extractUserRelPath(raw);
      if (userRel && !isSkillWorkspacePath(raw) && !/^https?:/i.test(raw)) {
        const key = normalizeWorkspacePath(raw);
        if (key && !seen.has(key)) {
          seen.add(key);
          found.push(raw);
        }
      }
      m = re.exec(src);
    }
  }
  return found;
}
